import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Search, Plus, Edit2, Trash2, Eye, Star,
  Flame, Award, ChevronLeft, X, User, Loader2,
  Key, Copy, Check, RefreshCw, EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useStudents, useCreateStudent, useUpdateStudent, useDeleteStudent } from '../../hooks/useTeacher';
import studentService from '../../services/studentService';

// Sample students data (fallback)
const INITIAL_STUDENTS = [];

const AVATAR_OPTIONS = [
  { id: 'boy', emoji: '👦', label: 'Boy' },
  { id: 'girl', emoji: '👧', label: 'Girl' },
  { id: 'seedling', emoji: '🌱', label: 'Seedling' },
  { id: 'tree', emoji: '🌳', label: 'Tree' },
  { id: 'flower', emoji: '🌻', label: 'Flower' },
  { id: 'bee', emoji: '🐝', label: 'Bee' },
];

const GRADE_OPTIONS = ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8'];

export default function Students() {
  // React Query hooks
  const { data: apiStudents, isLoading, isError, refetch } = useStudents();
  const createStudent = useCreateStudent();
  const updateStudent = useUpdateStudent();
  const deleteStudent = useDeleteStudent();

  // Local state - use API data or fallback to sample data
  const [students, setStudents] = useState(INITIAL_STUDENTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [showPins, setShowPins] = useState({});
  const [copiedPin, setCopiedPin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    avatar: 'seedling',
    grade: 'Grade 5',
    pin: '',
  });

  // Update local state when API data is available
  useEffect(() => {
    if (apiStudents) {
      setStudents(apiStudents);
    }
  }, [apiStudents]);

  const filteredStudents = students.filter((student) =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAvatarEmoji = (avatar) => {
    const option = AVATAR_OPTIONS.find((o) => o.id === avatar);
    return option ? option.emoji : '🌱';
  };

  const handleOpenModal = (student = null) => {
    if (student) {
      setEditingStudent(student);
      setFormData({
        name: student.name,
        avatar: student.avatar,
        grade: student.grade,
        pin: student.pin || '',
      });
    } else {
      setEditingStudent(null);
      // Generate a random 4-digit PIN for new students
      const randomPin = Math.floor(1000 + Math.random() * 9000).toString();
      setFormData({
        name: '',
        avatar: 'seedling',
        grade: 'Grade 5',
        pin: randomPin,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingStudent(null);
    setFormData({ name: '', avatar: 'seedling', grade: 'Grade 5', pin: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter a student name');
      return;
    }

    if (!formData.pin || formData.pin.length !== 4) {
      toast.error('PIN must be 4 digits');
      return;
    }

    try {
      if (editingStudent) {
        // Try API first, fallback to local state
        if (apiStudents) {
          await updateStudent.mutateAsync({
            studentId: editingStudent.id,
            data: formData,
          });
        } else {
          // Local fallback
          setStudents((prev) =>
            prev.map((s) =>
              s.id === editingStudent.id
                ? { ...s, ...formData }
                : s
            )
          );
          toast.success('Student updated successfully');
        }
      } else {
        // Try API first, fallback to local state
        if (apiStudents) {
          await createStudent.mutateAsync(formData);
        } else {
          // Local fallback
          const newStudent = {
            id: Date.now(),
            ...formData,
            points: 0,
            streak: 0,
            badges: 0,
            week: 1,
            status: 'active',
          };
          setStudents((prev) => [...prev, newStudent]);
          toast.success('Student added successfully');
        }
      }
      handleCloseModal();
    } catch (error) {
      // Error already handled by mutation hooks
    }
  };

  const handleDelete = async (studentId) => {
    if (window.confirm('Are you sure you want to remove this student?')) {
      try {
        if (apiStudents) {
          await deleteStudent.mutateAsync(studentId);
        } else {
          // Local fallback
          setStudents((prev) => prev.filter((s) => s.id !== studentId));
          toast.success('Student removed');
        }
      } catch (error) {
        // Error already handled by mutation hooks
      }
    }
  };

  const handleTogglePin = (studentId) => {
    setShowPins(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const handleCopyPin = async (studentId, pin) => {
    try {
      await navigator.clipboard.writeText(pin);
      setCopiedPin(studentId);
      setTimeout(() => setCopiedPin(null), 2000);
      toast.success('PIN copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy PIN');
    }
  };

  const handleResetPin = async (studentId) => {
    if (window.confirm('Generate a new random PIN for this student?')) {
      try {
        if (apiStudents) {
          await studentService.resetStudentPin(studentId);
          refetch(); // Refresh the list
          toast.success('PIN has been reset');
        } else {
          // Local fallback
          const newPin = Math.floor(1000 + Math.random() * 9000).toString();
          setStudents((prev) =>
            prev.map((s) =>
              s.id === studentId
                ? { ...s, pin: newPin }
                : s
            )
          );
          toast.success(`New PIN: ${newPin}`);
        }
      } catch (error) {
        toast.error('Failed to reset PIN');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-leaf-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading students...</p>
        </div>
      </div>
    );
  }

  const isMutating = createStudent.isPending || updateStudent.isPending || deleteStudent.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Link
              to="/teacher"
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </Link>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Manage Students</h1>
              <p className="text-gray-600">{students.length} students enrolled</p>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={20} />
              Add Student
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-leaf-500 focus:ring-2 focus:ring-leaf-200 transition-all"
            />
          </div>
        </div>

        {/* Students Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStudents.map((student, index) => (
            <motion.div
              key={student.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <div className="p-6">
                {/* Student Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-leaf-100 flex items-center justify-center text-3xl">
                    {getAvatarEmoji(student.avatar)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{student.name}</h3>
                    <p className="text-sm text-gray-500">{student.grade}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium
                      ${(student.is_active !== false && student.status !== 'inactive') ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {(student.is_active !== false && student.status !== 'inactive') ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* PIN Display */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Key size={16} className="text-gray-400" />
                      <span className="text-sm text-gray-600">PIN:</span>
                      <span className="font-mono font-bold text-lg">
                        {showPins[student.id] ? (student.pin || '----') : '****'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleTogglePin(student.id)}
                        className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                        title={showPins[student.id] ? 'Hide PIN' : 'Show PIN'}
                      >
                        {showPins[student.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button
                        onClick={() => handleCopyPin(student.id, student.pin)}
                        className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                        title="Copy PIN"
                        disabled={!showPins[student.id]}
                      >
                        {copiedPin === student.id ? (
                          <Check size={16} className="text-green-600" />
                        ) : (
                          <Copy size={16} className={showPins[student.id] ? '' : 'text-gray-300'} />
                        )}
                      </button>
                      <button
                        onClick={() => handleResetPin(student.id)}
                        className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                        title="Reset PIN"
                      >
                        <RefreshCw size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-sunshine-500">
                      <Star size={14} />
                      <span className="font-semibold">{student.total_points || student.points || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500">Points</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-orange-500">
                      <Flame size={14} />
                      <span className="font-semibold">{student.streak_days || student.streak || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500">Streak</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-purple-500">
                      <Award size={14} />
                      <span className="font-semibold">{student.badges_earned || student.badges || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500">Badges</p>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Week Progress</span>
                    <span className="font-medium">Week {student.current_week || student.week || 1}/36</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-leaf-500 rounded-full"
                      style={{ width: `${((student.current_week || student.week || 1) / 36) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Link
                    to={`/teacher/students/${student.id}`}
                    className="flex-1 btn-ghost py-2 flex items-center justify-center gap-1 text-sm"
                  >
                    <Eye size={16} />
                    View
                  </Link>
                  <button
                    onClick={() => handleOpenModal(student)}
                    className="flex-1 btn-ghost py-2 flex items-center justify-center gap-1 text-sm"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(student.id)}
                    className="btn-ghost py-2 px-3 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredStudents.length === 0 && (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No students found</p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-2 text-leaf-600 hover:text-leaf-700"
              >
                Clear search
              </button>
            )}
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold">
                  {editingStudent ? 'Edit Student' : 'Add New Student'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit} className="p-6">
                {/* Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter student name"
                      className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-leaf-500 focus:ring-2 focus:ring-leaf-200"
                    />
                  </div>
                </div>

                {/* Avatar */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Avatar
                  </label>
                  <div className="grid grid-cols-6 gap-2">
                    {AVATAR_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, avatar: option.id })}
                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all
                          ${formData.avatar === option.id
                            ? 'bg-leaf-100 ring-2 ring-leaf-500'
                            : 'bg-gray-100 hover:bg-gray-200'}`}
                      >
                        {option.emoji}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Grade */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grade
                  </label>
                  <select
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-leaf-500 focus:ring-2 focus:ring-leaf-200"
                  >
                    {GRADE_OPTIONS.map((grade) => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>

                {/* PIN */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student PIN (4 digits) *
                  </label>
                  <div className="relative">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      value={formData.pin}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setFormData({ ...formData, pin: value });
                      }}
                      placeholder="4-digit PIN"
                      maxLength={4}
                      className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-leaf-500 focus:ring-2 focus:ring-leaf-200 font-mono text-lg tracking-widest"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Students will use this PIN along with their name and class code to sign in
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 btn-ghost py-3"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-primary py-3"
                    disabled={isMutating}
                  >
                    {isMutating ? (
                      <Loader2 size={20} className="animate-spin mx-auto" />
                    ) : editingStudent ? 'Save Changes' : 'Add Student'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
